import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import AnalyticsDisplay from '../components/AnalyticsDisplay';
import { useLocation } from 'react-router-dom';
import SidebarNav from '../components/SidebarNav';

const AnalyticsPage = () => {
    const { t } = useTranslation();
    const location = useLocation();

    // --- State Variables ---
    const [analytics, setAnalytics] = useState(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(true);
    const [error, setError] = useState(null);

    // --- Theming ---
    const textPrimary = "text-silver-light";
    const textSecondary = "text-silver-medium/80";

    // --- Fetching Logic ---
    useEffect(() => {
        const fetchAnalyticsData = async () => {
            setLoadingAnalytics(true);
            try {
                const analyticsRes = await axios.get('/api/analytics/summary');
                if (analyticsRes.data && analyticsRes.data.success) {
                    setAnalytics(analyticsRes.data.data);
                } else {
                    throw new Error(analyticsRes.data.message || 'Failed to fetch analytics');
                }
            } catch (err) {
                setError(`Error fetching analytics: ${err.message}`);
                console.error("Analytics Fetch Error:", err);
            } finally {
                setLoadingAnalytics(false);
            }
        };

        fetchAnalyticsData();
    }, []);

    // --- Intersection Observer Animations ---
    const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.1 });

    return (
        <div className="bg-black min-h-screen font-sans">
            {/* Sidebar */}
            <SidebarNav currentPath={location.pathname} />

            {/* Main Content Area */}
            <div className="ml-64">
                {/* Hero Section */}
                <section
                    ref={heroRef}
                    className="pt-36 pb-16 bg-black"
                >
                    <div className="container mx-auto px-6">
                        <div
                            className={`max-w-3xl mx-auto text-center transition-all duration-1000 transform ${heroInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                        >
                            <h1 className={`text-5xl md:text-6xl font-serif font-medium mb-6 ${textPrimary}`}>
                                {t('developerPage.analyticsTitle', 'Site Analytics')}
                            </h1>
                            <p className={`text-lg md:text-xl ${textSecondary} mb-8 leading-relaxed`}>
                                {t('developerPage.analyticsSubtitle', 'Detailed insights into website traffic and user engagement.')}
                            </p>
                            <div className="mt-3 w-40 h-1 mx-auto bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-50"></div>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section
                    ref={contentRef}
                    className={`py-8 transition-all duration-1000 transform ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                >
                    <div className="container mx-auto px-6 space-y-8">
                        {/* Display Error if any */}
                        {error && (
                            <div className="max-w-5xl mx-auto bg-red-900/20 border border-red-700/30 text-red-300 px-5 py-4 rounded-lg mb-6">
                                <div className="flex items-center space-x-3 mb-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h3 className="font-bold text-sm">{t('developerPage.errorLoadingData', 'Error Loading Data')}</h3>
                                </div>
                                <pre className="text-xs whitespace-pre-wrap">{error}</pre>
                            </div>
                        )}

                        {/* Analytics Content Area */}
                        <div className="max-w-5xl mx-auto bg-gray-900/60 rounded-lg border border-gray-800/50 overflow-hidden shadow-2xl backdrop-blur-sm">
                            <div className="p-6">
                                <h2 className={`text-2xl font-medium mb-6 ${textPrimary} flex items-center`}>
                                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                                    </svg>
                                    {t('developerPage.analyticsSummaryTitle', 'Analytics Summary')}
                                </h2>
                                {loadingAnalytics ? (
                                    <div className="py-8">
                                        <div className="animate-pulse flex flex-col space-y-6">
                                            <div className="h-12 bg-gray-800/70 rounded-md w-3/4"></div>
                                            <div className="h-64 bg-gray-800/70 rounded-md w-full"></div>
                                            <div className="h-24 bg-gray-800/70 rounded-md w-full"></div>
                                        </div>
                                    </div>
                                ) : analytics ? (
                                    <AnalyticsDisplay analyticsData={analytics} />
                                ) : (
                                    <div className="py-8 text-center">
                                        <p className={`${textSecondary} mb-2`}>{t('developerPage.noAnalyticsData', 'No analytics data available.')}</p>
                                        <p className="text-sm text-gray-500">Analytics data will appear here once visitors start accessing your site.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default AnalyticsPage; 